import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageLoader } from "@/components/Kit";
import { Users, UserPlus, Search, Check, X, MessageSquare, UserMinus } from "lucide-react";

export default function Friends() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);

  const load = () => api.get("/friends").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);

  const search = async (val) => {
    setQ(val);
    if (val.trim().length < 2) { setResults([]); return; }
    try { const { data } = await api.get(`/users/search?q=${encodeURIComponent(val)}`); setResults(data.users); } catch { /* */ }
  };
  const request = async (id) => {
    try { await api.post("/friends/request", { to_user: id }); toast.success("Request sent."); setResults([]); setQ(""); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const respond = async (req_id, accept) => {
    try { await api.post("/friends/respond", { req_id, accept }); toast.success(accept ? "Friend added!" : "Request declined."); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/friends/${id}`); toast.success("Removed."); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!d) return <PageLoader label="Loading comms roster" />;

  return (
    <div data-testid="friends-page">
      <span className="label-cap text-primary flex items-center gap-2"><Users className="w-4 h-4" /> Comms Roster</span>
      <h1 className="text-4xl font-black tracking-tighter mt-2 mb-5 font-head">Friends</h1>

      <div className="panel p-4 mb-5">
        <div className="label-cap mb-2 flex items-center gap-1.5"><Search className="w-3.5 h-3.5" /> Find Pilots</div>
        <input data-testid="friend-search" value={q} onChange={(e) => search(e.target.value)} placeholder="Search by name…"
          className="w-full px-3 py-2.5 rounded-[var(--radius)] bg-muted border border-border text-sm focus:outline-none focus:border-primary" />
        {results.length > 0 && (
          <div className="mt-2 space-y-1">
            {results.map((r) => (
              <div key={r.user_id} data-testid={`search-${r.user_id}`} className="flex items-center justify-between px-2 py-2 rounded-[var(--radius)] hover:bg-muted">
                <button onClick={() => navigate(`/app/profile/${r.user_id}`)} className="text-sm font-semibold hover:text-primary">{r.name}</button>
                <button onClick={() => request(r.user_id)} data-testid={`req-${r.user_id}`} className="btn-primary text-xs"><UserPlus className="w-3.5 h-3.5" /> Add</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {d.incoming.length > 0 && (
        <div className="panel p-4 mb-5">
          <div className="label-cap mb-2 text-accent">Incoming Requests ({d.incoming.length})</div>
          <div className="space-y-2">
            {d.incoming.map((r) => (
              <div key={r.req_id} data-testid={`incoming-${r.req_id}`} className="flex items-center justify-between">
                <span className="text-sm font-semibold">{r.from_name}</span>
                <div className="flex gap-2">
                  <button onClick={() => respond(r.req_id, true)} data-testid={`accept-friend-${r.req_id}`} className="btn-primary text-xs"><Check className="w-3.5 h-3.5" /> Accept</button>
                  <button onClick={() => respond(r.req_id, false)} data-testid={`decline-friend-${r.req_id}`} className="btn-ghost text-xs"><X className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="panel p-4">
        <div className="label-cap mb-3">Your Friends ({d.friends.length})</div>
        {d.friends.length === 0 ? <p className="text-sm text-muted-foreground">No friends yet. Search above to connect with other pilots.</p> : (
          <div className="grid sm:grid-cols-2 gap-2">
            {d.friends.map((f) => (
              <div key={f.user_id} data-testid={`friend-${f.user_id}`} className="flex items-center justify-between panel p-3">
                <button onClick={() => navigate(`/app/profile/${f.user_id}`)} className="text-sm font-semibold hover:text-primary">{f.name}</button>
                <div className="flex gap-1.5">
                  <button onClick={() => navigate(`/app/mail?to=${f.user_id}`)} className="btn-ghost text-xs p-1.5"><MessageSquare className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(f.user_id)} data-testid={`remove-${f.user_id}`} className="btn-ghost text-xs p-1.5 text-accent"><UserMinus className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
